import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Box, CircularProgress, Typography } from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import { PartiesRoutes } from '../../../Router/path';
import PartyForm from '../Components/PartyForm/PartyForm';
import { partyInitialValues } from '../initialValues';
import { extractPartiesErrorFeedback } from '../Service/partiesMapper';
import { useGetPartyByIdQuery, useUpdatePartyMutation } from '../Service/partiesApi';
import { mapPartyDtoToFormValues, mapPartyFormValuesToRequest } from '../Types/partyTypes';

const EditParty = () => {
  const navigate = useNavigate();
  const params = useParams<{ partyId: string }>();
  const rawPartyId = params.partyId ?? '';
  const hasStrictNumericPartyId = /^\d+$/.test(rawPartyId);
  const parsedPartyId = hasStrictNumericPartyId ? Number(rawPartyId) : Number.NaN;
  const hasInvalidPartyId = !hasStrictNumericPartyId || parsedPartyId <= 0;
  const [submitErrorMessage, setSubmitErrorMessage] = useState<string | null>(null);
  const [updateParty, updateStatus] = useUpdatePartyMutation();

  const {
    data: partyDetails,
    isLoading: isPartyLoading,
    isError: isPartyError,
    error: partyLoadError,
  } = useGetPartyByIdQuery(parsedPartyId, {
    skip: hasInvalidPartyId,
    refetchOnMountOrArgChange: true,
  });

  useEffect(() => {
    if (!updateStatus.isSuccess) {
      return;
    }

    const timeout = window.setTimeout(() => {
      navigate(PartiesRoutes.root);
    }, 1200);

    return () => window.clearTimeout(timeout);
  }, [navigate, updateStatus.isSuccess]);

  const initialValues = useMemo(
    () => (partyDetails ? mapPartyDtoToFormValues(partyDetails) : partyInitialValues),
    [partyDetails]
  );

  if (hasInvalidPartyId) {
    return (
      <Alert severity="error">
        Nieprawidłowy identyfikator strony. Wróć do listy i wybierz rekord ponownie.
      </Alert>
    );
  }

  if (isPartyLoading) {
    return (
      <Box display="flex" justifyContent="center" mt={4}>
        <CircularProgress />
      </Box>
    );
  }

  if (isPartyError) {
    const feedback = extractPartiesErrorFeedback(
      partyLoadError,
      'Nie udało się pobrać danych strony do edycji.'
    );

    return (
      <Alert severity="error">
        <Typography>{feedback.message}</Typography>
      </Alert>
    );
  }

  return (
    <PartyForm
      mode="edit"
      initialValues={initialValues}
      isSubmitting={updateStatus.isLoading}
      isSuccess={updateStatus.isSuccess}
      submitErrorMessage={submitErrorMessage}
      onSubmit={async (values) => {
        setSubmitErrorMessage(null);

        try {
          await updateParty({ id: parsedPartyId, data: mapPartyFormValuesToRequest(values) }).unwrap();
        } catch (error) {
          const feedback = extractPartiesErrorFeedback(
            error,
            'Nie udało się zapisać zmian strony. Zweryfikuj formularz i spróbuj ponownie.'
          );
          setSubmitErrorMessage(feedback.message);
        }
      }}
      onCancel={() => navigate(PartiesRoutes.root)}
    />
  );
};

export default EditParty;
